'use client'

import { NativeSelect } from '@/components/ui/native-select'
import { Toggle } from '@/components/ui/toggle'
import { useUserSettings } from '@/components/user-settings-context'
import type { UserSettings } from '@/lib/user-settings'
import { SettingRow } from './setting-row'

// Font options available in Monaco
const FONT_OPTIONS = [
  { value: 'default', label: 'Default' },
  { value: 'fira-code', label: 'Fira Code' },
]

const FONT_SIZE_OPTIONS = [
  { value: '12', label: '12px' },
  { value: '13', label: '13px' },
  { value: '14', label: '14px' },
  { value: '15', label: '15px' },
  { value: '16', label: '16px' },
  { value: '18', label: '18px' },
  { value: '20', label: '20px' },
]

const KEY_BINDING_OPTIONS = [
  { value: 'standard', label: 'Standard' },
  { value: 'vim', label: 'Vim' },
]

const TAB_SIZE_OPTIONS = [
  { value: '2', label: '2 spaces' },
  { value: '4', label: '4 spaces' },
]

const SUBMIT_SOUND_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'award', label: 'Award' },
  { value: 'goal', label: 'Goal' },
  { value: 'festive-melody', label: 'Festive Melody' },
  { value: 'positive-notification', label: 'Positive Notification' },
  { value: 'trumpet-fanfare', label: 'Trumpet Fanfare' },
  { value: 'video-game-treasure', label: 'Video Game Treasure' },
  { value: 'item-acquired', label: 'Item Acquired' },
]

/** Playback config for submit sounds that need special handling */
interface SubmitSoundConfig {
  startAtSec?: number
}

const SUBMIT_SOUND_CONFIG: Partial<Record<string, SubmitSoundConfig>> = {}

export const SUBMIT_SOUND_MAP: Record<string, string> = {
  award: '/audio/success-submit/Award.wav',
  goal: '/audio/success-submit/Goal.wav',
  'festive-melody':
    '/audio/success-submit/mixkit-festive-melody-audio-2985.wav',
  'positive-notification':
    '/audio/success-submit/mixkit-positive-notification-951.wav',
  'trumpet-fanfare': '/audio/success-submit/mixkit-trumpet-fanfare-2293.wav',
  'video-game-treasure':
    '/audio/success-submit/mixkit-video-game-treasure-2066.wav',
  'item-acquired':
    '/audio/success-submit/zelda-oot-get-item-made-with-Voicemod.wav',
}

/** Play a submit sound with optional startAtSec offset */
export function playSubmitSound(soundKey: string): void {
  const url = SUBMIT_SOUND_MAP[soundKey]
  if (!url) return
  const config = SUBMIT_SOUND_CONFIG[soundKey]
  const audio = new Audio(url)
  if (config?.startAtSec) {
    audio.currentTime = config.startAtSec
  }
  void audio.play().catch(() => {})
}

/**
 * iOS/iPadOS Safari blocks Audio.play() outside a user gesture's call stack.
 * To work around this, we pre-create and load the Audio element during the
 * submit button tap (user gesture), then play it later when the async success
 * callback fires.
 */
let primedAudio: HTMLAudioElement | null = null

/** Call from a user gesture handler (e.g. submit button onClick) to prime audio for iOS. */
export function primeSubmitSound(soundKey: string): void {
  const url = SUBMIT_SOUND_MAP[soundKey]
  if (!url) {
    primedAudio = null
    return
  }
  const config = SUBMIT_SOUND_CONFIG[soundKey]
  const audio = new Audio(url)
  audio.preload = 'auto'
  if (config?.startAtSec) {
    audio.currentTime = config.startAtSec
  }
  primedAudio = audio
}

/** Play the primed audio element. Falls back to playSubmitSound if nothing was primed. */
export function playPrimedSubmitSound(soundKey: string): void {
  if (primedAudio) {
    const audio = primedAudio
    primedAudio = null
    void audio.play().catch(() => {})
  } else {
    playSubmitSound(soundKey)
  }
}

const LANGUAGE_OPTIONS = [
  { value: 'python', label: 'Python' },
  { value: 'javascript', label: 'JavaScript (Coming Soon)', disabled: true },
  { value: 'java', label: 'Java (Coming Soon)', disabled: true },
  { value: 'cpp', label: 'C++ (Coming Soon)', disabled: true },
  { value: 'go', label: 'Go (Coming Soon)', disabled: true },
]

export function CodeEditorSettings() {
  const { settings, updateSettings } = useUserSettings()
  const editor = settings.editor

  const update = (partial: Partial<UserSettings['editor']>) =>
    updateSettings({ editor: partial })

  const handleSubmitSoundChange = (value: string) => {
    update({
      successSubmitSound: value as UserSettings['editor']['successSubmitSound'],
    })
    playSubmitSound(value)
  }

  return (
    <div className="space-y-1">
      <SettingRow label="Default language">
        <NativeSelect
          value={editor.defaultLanguage}
          onChange={(v) =>
            update({
              defaultLanguage: v as UserSettings['editor']['defaultLanguage'],
            })
          }
          options={LANGUAGE_OPTIONS}
        />
      </SettingRow>

      <div>
        <SettingRow label="Font">
          <NativeSelect
            value={editor.font}
            onChange={(v) =>
              update({ font: v as UserSettings['editor']['font'] })
            }
            options={FONT_OPTIONS}
          />
        </SettingRow>

        <SettingRow label="Font size">
          <NativeSelect
            value={String(editor.fontSize)}
            onChange={(v) => update({ fontSize: Number(v) })}
            options={FONT_SIZE_OPTIONS}
          />
        </SettingRow>

        <SettingRow label="Font ligatures">
          <Toggle
            checked={editor.fontLigatures}
            onChange={(v) => update({ fontLigatures: v })}
            aria-label="Toggle font ligatures"
          />
        </SettingRow>
      </div>

      <div>
        <SettingRow label="Key binding">
          <NativeSelect
            value={editor.keyBinding}
            onChange={(v) =>
              update({
                keyBinding: v as UserSettings['editor']['keyBinding'],
              })
            }
            options={KEY_BINDING_OPTIONS}
          />
        </SettingRow>
      </div>

      <SettingRow label="Tab size">
        <NativeSelect
          value={String(editor.tabSize)}
          onChange={(v) => update({ tabSize: Number(v) as 2 | 4 })}
          options={TAB_SIZE_OPTIONS}
        />
      </SettingRow>

      <SettingRow label="Word wrap">
        <Toggle
          checked={editor.wordWrap}
          onChange={(v) => update({ wordWrap: v })}
          aria-label="Toggle word wrap"
        />
      </SettingRow>

      <SettingRow label="Relative line numbers">
        <Toggle
          checked={editor.relativeLineNumbers}
          onChange={(v) => update({ relativeLineNumbers: v })}
          aria-label="Toggle relative line numbers"
        />
      </SettingRow>

      <SettingRow label="Successful submit sound">
        <NativeSelect
          value={editor.successSubmitSound}
          onChange={handleSubmitSoundChange}
          options={SUBMIT_SOUND_OPTIONS}
        />
      </SettingRow>
    </div>
  )
}
